import {Button, Col, Form, Row, Spinner} from 'react-bootstrap'
import {useFormik} from 'formik'
import * as yup from 'yup'
import {useMutation} from '@apollo/client'
import {ADD_EDIT_VALIDATION} from '../../../../graphql/mutations/userMutations'
import {parseId} from '../../../../helpers'
import {useEffect} from 'react'
import {toast} from 'react-toastify'
import {INodeIdentities, IResAddEditVal} from '../../../../graphql/types/usersTypes'
import {NodeValidations} from '../../../../graphql/types/validationsTypes'
import {useNavigate} from 'react-router-dom'

const validationSchema = yup.object({
  firstName: yup.string().required('El primer nombre es requerido'),
  firstSurname: yup.string().required('El primer apellido es requerido'),
  documentType: yup.string().required('El tipo de documento es requerido'),
  documentNumber: yup
    .string()
    .min(4, 'Demasiado corta')
    .required('El número de documento es requerido'),
  expeditionDate: yup.date().required('La fecha de expedición es requerida'),
  /* .max(
      new Date(new Date().setFullYear(new Date().getFullYear() - 18)),
      'Debes ser mayor de edad (18 años mínimo)'
    ) */ phoneNumber: yup
    .string()
    .min(8, 'Demasiado corto')
    .required('El número de teléfono es requerido'),
})

interface IAddVal {
  id?: string
  refetch: () => void
  onToggleSubTab: (bool?: boolean) => void
  dataPrev?: INodeIdentities | NodeValidations
  hiddenEffect?: boolean
  cancelBtn?: JSX.Element
}

export const AddValidation: React.FC<IAddVal> = ({
  id,
  onToggleSubTab,
  refetch,
  dataPrev,
  hiddenEffect,
  cancelBtn,
}) => {
  const [gqlAddEdit, {data, loading, error}] = useMutation<IResAddEditVal>(ADD_EDIT_VALIDATION)
  const navigate = useNavigate()
  const formik = useFormik({
    initialValues: {
      firstName: '',
      secondName: '',
      firstSurname: '',
      secondSurname: '',
      documentType: '',
      documentNumber: '',
      expeditionDate: '',
      phoneNumber: '',
    },
    validationSchema,
    onSubmit: (values) => {
      console.log('Formulario enviado:', values)
      gqlAddEdit({
        variables: {
          input: {
            ...values,
            id: id ? parseId(id) : undefined,
          },
        },
      })
    },
  })

  useEffect(() => {
    if (!dataPrev) return
    formik.setValues({
      firstName: dataPrev.firstName,
      secondName: dataPrev.secondName || '',
      firstSurname: dataPrev.firstSurname,
      secondSurname: dataPrev.secondSurname || '',
      phoneNumber: dataPrev.phoneNumber,
      documentNumber: dataPrev.documentNumber,
      documentType: dataPrev.documentType,
      expeditionDate: dataPrev.expeditionDate,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataPrev])

  useEffect(() => {
    if (error) toast.error(`Error: ${error}`)
  }, [error])

  useEffect(() => {
    if (!data) return
    if (data.identityValidationCreateUpdate?.identityValidation?.id) {
      toast.success(`Validación ${dataPrev ? 'editada' : 'agregada'} correctamente`)
      onToggleSubTab()
      if (!id) {
        navigate(
          `/validation/${parseId(data.identityValidationCreateUpdate?.identityValidation?.id)}`
        )
      } else {
        refetch()
      }
    } else if (data.identityValidationCreateUpdate.errors?.length > 0) {
      toast.error(
        `Error: ${data.identityValidationCreateUpdate.errors[0].messages?.[0] || 'Algo ocurrió'}`
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  return (
    <Form
      onSubmit={formik.handleSubmit}
      className={hiddenEffect ? '' : 'animation animation-slide-in-up'}
    >
      <Row>
        <Col sm={12} md={6}>
          <Form.Group className='mb-3'>
            <Form.Label>Primer nombre</Form.Label>
            <Form.Control
              type='text'
              name='firstName'
              value={formik.values.firstName}
              onChange={formik.handleChange}
              isInvalid={!!formik.errors.firstName}
            />
            <Form.Control.Feedback type='invalid'>{formik.errors.firstName}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col sm={12} md={6}>
          <Form.Group className='mb-3'>
            <Form.Label>Segundo nombre</Form.Label>
            <Form.Control
              type='text'
              name='secondName'
              value={formik.values.secondName}
              onChange={formik.handleChange}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col sm={12} md={6}>
          <Form.Group className='mb-3'>
            <Form.Label>Primer apellido</Form.Label>
            <Form.Control
              type='text'
              name='firstSurname'
              value={formik.values.firstSurname}
              onChange={formik.handleChange}
              isInvalid={!!formik.errors.firstSurname}
            />
            <Form.Control.Feedback type='invalid'>
              {formik.errors.firstSurname}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col sm={12} md={6}>
          <Form.Group className='mb-3'>
            <Form.Label>Segundo apellido</Form.Label>
            <Form.Control
              type='text'
              name='secondSurname'
              value={formik.values.secondSurname}
              onChange={formik.handleChange}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col sm={3} md={5}>
          <Form.Group className='mb-3'>
            <Form.Label>Tipo de documento</Form.Label>
            <Form.Select
              name='documentType'
              value={formik.values.documentType}
              onChange={formik.handleChange}
              isInvalid={!!formik.errors.documentType}
            >
              <option value=''>Seleccione uno</option>
              <option value='CC'>Cédula ciudadanía</option>
              <option value='CE'>Cédula extranjería</option>
            </Form.Select>
            <Form.Control.Feedback type='invalid'>
              {formik.errors.documentType}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col sm={9} md={7}>
          <Form.Group className='mb-3'>
            <Form.Label>Número de documento</Form.Label>
            <Form.Control
              type='text'
              name='documentNumber'
              value={formik.values.documentNumber}
              onChange={(e) => {
                const {value} = e.target

                if (value === '') {
                  formik.setFieldValue('documentNumber', '')
                  return
                }
                if (isNaN(Number(value))) return

                formik.setFieldValue('documentNumber', value)
              }}
              isInvalid={!!formik.errors.documentNumber}
            />
            <Form.Control.Feedback type='invalid'>
              {formik.errors.documentNumber}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col sm={5}>
          <Form.Group className='mb-3'>
            <Form.Label>Fecha de expedición</Form.Label>
            <Form.Control
              type='date'
              name='expeditionDate'
              value={formik.values.expeditionDate}
              onChange={formik.handleChange}
              isInvalid={!!formik.errors.expeditionDate}
            />
            <Form.Control.Feedback type='invalid'>
              {formik.errors.expeditionDate}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col sm={7}>
          <Form.Group className='mb-3'>
            <Form.Label>Número de teléfono</Form.Label>
            <Form.Control
              type='text'
              name='phoneNumber'
              value={formik.values.phoneNumber}
              onChange={(e) => {
                const {value} = e.target

                if (value === '') {
                  formik.setFieldValue('phoneNumber', '')
                  return
                }
                if (isNaN(Number(value))) return

                formik.setFieldValue('phoneNumber', value)
              }}
              isInvalid={!!formik.errors.phoneNumber}
            />
            <Form.Control.Feedback type='invalid'>
              {formik.errors.phoneNumber}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <div className='mt-4 d-flex w-100 align-items-center justify-content-end gap-3'>
        {cancelBtn || null}
        <Button variant='info' type='submit' disabled={loading}>
          {loading && <Spinner animation='border' size='sm' className='me-3' />}
          {dataPrev ? 'Editar' : 'Agregar'}
        </Button>
      </div>
    </Form>
  )
}
